import { supabase } from "@/services/supabase/createClient";
import { PSResourceType } from "@pufflig/prompt-studio-types";
import { useQuery, useQueryClient } from "@tanstack/react-query";

export const getResourceById = async (resourceId: string) => {
  const { data, error } = await supabase.from("resources").select("*").eq("resource_id", resourceId).single();

  if (error) {
    throw error;
  }

  return data;
};

export const useResourceById = (resourceId: string) => {
  return useQuery({
    queryKey: ["resources", resourceId],
    queryFn: () => getResourceById(resourceId),
  });
};

export const getResources = async (workspaceId: string, resourceType: PSResourceType) => {
  const { data, error } = await supabase.rpc("get_workspace_resources", {
    p_workspace_id: workspaceId,
    p_type: resourceType,
  });

  if (error) {
    throw error;
  }

  return data;
};

export const useWorkspaceResources = (workspaceId: string, resourceType: PSResourceType) => {
  return useQuery({
    queryKey: ["resources", workspaceId, resourceType],
    queryFn: () => getResources(workspaceId, resourceType),
  });
};

export const useResourceSubscription = () => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["resources_subscription"],
    queryFn: () => {
      const channels = supabase
        .channel("resources_all")
        .on("postgres_changes", { event: "*", schema: "public", table: "resources" }, () => {
          queryClient.invalidateQueries({
            predicate: (query) => query.queryKey?.[0] === "resources",
          });
        })
        .subscribe();
      return () => {
        channels.unsubscribe();
      };
    },
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};
